/**
 * FP3-060 — IEP/MET Due Dates Hub.
 *
 * Compliance-calendar surface for provider / solo provider / org
 * admin / super_admin. Single API call to /api/due-dates returns
 * scoped summary counts + per-row status; the page renders a
 * dashboard card band, quick-range pills, filters, and a sortable
 * table. Month grouping is shown as collapsible sections at the
 * bottom — the spec asks for a "simple calendar/month grouping if
 * low-risk" so we ship table + month groups in this pass and park
 * the full month-grid for a follow-on.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  CalendarBlank,
  CheckCircle,
  Warning,
  Clock,
  CaretRight,
  DownloadSimple,
  FunnelSimple,
} from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useDueDates } from "@/lib/adminQueries";
import { RefreshChip } from "@/lib/formatUpdatedAgo";

const STATUS_LABEL = {
  overdue: "Overdue",
  due_soon: "Due soon",
  upcoming: "Upcoming",
  missing: "Missing date",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return iso;
  }
}

function ymd(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function rangePreset(key) {
  const today = new Date();
  const start = new Date(today);
  const end = new Date(today);
  if (key === "this_week") {
    const day = start.getDay(); // 0=Sun
    start.setDate(start.getDate() - day);
    end.setDate(end.getDate() + (6 - day));
  } else if (key === "next_30") {
    end.setDate(end.getDate() + 30);
  } else if (key === "next_60") {
    end.setDate(end.getDate() + 60);
  } else if (key === "next_90") {
    end.setDate(end.getDate() + 90);
  } else if (key === "this_school_year") {
    // School year = current Aug 1 – next Jul 31 (US calendar default).
    const y = today.getMonth() >= 7 ? today.getFullYear() : today.getFullYear() - 1;
    start.setFullYear(y, 7, 1);
    end.setFullYear(y + 1, 6, 31);
  } else {
    return { start: "", end: "" };
  }
  return { start: ymd(start), end: ymd(end) };
}

function StatusPill({ status, daysOffset }) {
  const styles = {
    overdue: "bg-[#FBEFE8] text-[#7A3A2C]",
    due_soon: "bg-[#FBEDD4] text-[#7A5B1F]",
    upcoming: "bg-[#E8F0EB] text-[#1E3F33]",
    missing: "bg-[#F3F1EC] text-[#6B7280]",
  };
  const cls = styles[status] || styles.missing;
  const label = STATUS_LABEL[status] || status;
  let detail = "";
  if (status === "overdue" && daysOffset !== null && daysOffset !== undefined) {
    detail = ` · ${Math.abs(daysOffset)}d`;
  } else if (status === "due_soon" && daysOffset !== null && daysOffset !== undefined) {
    detail = ` · in ${daysOffset}d`;
  }
  return (
    <span
      data-testid={`due-dates-row-status-${status}`}
      className={`inline-flex items-center text-[10.5px] uppercase tracking-[0.08em] font-semibold rounded-full px-2 py-0.5 ${cls}`}
    >
      {label}{detail}
    </span>
  );
}

function SummaryCard({ label, value, tone = "neutral", testid }) {
  const valueColor = {
    danger: "text-[#7A3A2C]",
    warn: "text-[#7A5B1F]",
    ok: "text-[#1E3F33]",
    neutral: "text-[#1F2937]",
  }[tone];
  return (
    <Card className="border-[#E5E3DB]" data-testid={testid}>
      <CardContent className="p-4">
        <p className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280]">
          {label}
        </p>
        <p className={`text-[22px] font-semibold tabular-nums mt-0.5 ${valueColor}`}>
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

// FP3-063 — Clickable column header for the Due Dates table. Active
// sort gets a directional arrow; inactive headers show a muted
// double-chevron hint so operators discover the affordance without
// the visual noise of a full sort glyph on every column. The
// click-twice-to-reverse contract is enforced by the parent via
// `onClick(col)`.
function SortableTh({ col, sort, onClick, children }) {
  const isActive = sort.col === col;
  const ariaSort = isActive
    ? sort.dir === "asc" ? "ascending" : "descending"
    : "none";
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className="text-left px-3 py-2 font-semibold"
    >
      <button
        type="button"
        onClick={() => onClick(col)}
        data-testid={`due-dates-sort-${col}`}
        data-active={isActive ? "true" : "false"}
        data-direction={isActive ? sort.dir : "none"}
        className={`inline-flex items-center gap-1 uppercase tracking-[0.08em] font-semibold text-[10.5px] ${
          isActive ? "text-[#1E3F33]" : "text-[#4B5563] hover:text-[#1E3F33]"
        } transition-colors`}
      >
        <span>{children}</span>
        <span aria-hidden="true" className="text-[9.5px] leading-none">
          {isActive ? (sort.dir === "asc" ? "▲" : "▼") : "▾"}
        </span>
      </button>
    </th>
  );
}

export default function DueDatesHub() {
  const [sp, setSp] = useSearchParams();

  // ── Filters ────────────────────────────────────────────────────
  const [filters, setFilters] = useState(() => ({
    start_date: sp.get("start_date") || "",
    end_date: sp.get("end_date") || "",
    due_type: sp.get("due_type") || "any",
    status: sp.get("status") || "any",
    grade: sp.get("grade") || "",
    campus: sp.get("campus") || "",
    include_archived: sp.get("include_archived") === "true",
  }));
  // Apply ?range= deep-link from Today widget on initial mount.
  useEffect(() => {
    const r = sp.get("range");
    if (r) {
      const { start, end } = rangePreset(r);
      setFilters((f) => ({ ...f, start_date: start, end_date: end }));
      // Clean the URL — keep date params, drop range.
      const next = new URLSearchParams(sp);
      next.delete("range");
      next.set("start_date", start);
      next.set("end_date", end);
      setSp(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FP3-119B — Steady-state cache. Params flow into the cache key so
  // each unique filter combination has its own slot; the query is
  // automatically re-issued when filters change (via the memoized
  // params object below). Manual Refresh forces a fresh authorized
  // request without discarding the cache; provider/admin/shadow
  // scope is preserved because the auth scope is embedded in the
  // key via `useDueDates`.
  const apiParams = useMemo(() => {
    const params = {};
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date) params.end_date = filters.end_date;
    if (filters.due_type && filters.due_type !== "any") params.due_type = filters.due_type;
    if (filters.status && filters.status !== "any") params.status = filters.status;
    if (filters.grade) params.grade = filters.grade;
    if (filters.campus) params.campus = filters.campus;
    if (filters.include_archived) params.include_archived = true;
    return params;
  }, [
    filters.start_date,
    filters.end_date,
    filters.due_type,
    filters.status,
    filters.grade,
    filters.campus,
    filters.include_archived,
  ]);
  const dueDatesQuery = useDueDates(apiParams);
  const data = dueDatesQuery.data || null;
  const loading = dueDatesQuery.isPending;
  const refetching = dueDatesQuery.isFetching && !dueDatesQuery.isPending;
  const dataUpdatedAt = dueDatesQuery.dataUpdatedAt;
  const error = dueDatesQuery.isError
    ? (() => {
        const detail =
          dueDatesQuery.error?.response?.data?.detail
          || dueDatesQuery.error?.message
          || "Could not load due dates.";
        return typeof detail === "string" ? detail : "Could not load due dates.";
      })()
    : "";
  const load = () => dueDatesQuery.refetch();

  const summary = data?.summary || null;
  const rawRows = data?.rows || [];

  // ── Sort state (FP3-063) ───────────────────────────────────────
  // Sorting is purely client-side over the rows the server already
  // returned for the active filter/range. Filters are NOT cleared by
  // sorting — the user sees the same rows in a new order.
  //
  // - Date columns sort on the raw ISO YYYY-MM-DD string (lexical =
  //   chronological). Missing dates always sink to the BOTTOM regardless
  //   of direction so empties never block compliance scanning.
  // - Status sorts by severity priority (overdue < due_soon <
  //   upcoming < missing) so ascending = "most urgent first".
  //   That matches what operators actually want when they click the
  //   Status header.
  // - Everything else is string-comparison with locale collation.
  //
  // The CSV export uses the SORTED rows (`rows` below), so what the
  // user sees is exactly what they download.
  const [sort, setSort] = useState({ col: "iep_due_date", dir: "asc" });
  const STATUS_PRIORITY = { overdue: 0, due_soon: 1, upcoming: 2, missing: 3 };
  const onHeaderClick = (col) => {
    setSort((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { col, dir: "asc" }
    );
  };
  const rowStatusPriority = (r) => {
    // Worst (lowest number) of IEP vs MET — same priority the UI uses
    // to pick the colour of the pill in the Status cell.
    const a = STATUS_PRIORITY[r.iep_status] ?? 3;
    const b = STATUS_PRIORITY[r.met_status] ?? 3;
    return Math.min(a, b);
  };
  const rows = useMemo(() => {
    if (!rawRows.length) return rawRows;
    const dir = sort.dir === "asc" ? 1 : -1;
    const collator = new Intl.Collator(undefined, {
      sensitivity: "base",
      numeric: true,
    });
    const compareDate = (a, b) => {
      // Treat missing as +Infinity so empties always sink to the
      // bottom regardless of the active direction.
      const av = a || "\uffff";
      const bv = b || "\uffff";
      if (av === bv) return 0;
      return av < bv ? -1 : 1;
    };
    const get = (r) => {
      switch (sort.col) {
        case "student":
          // last, first matches the rendered cell
          return [r.last_name || "", r.first_name || ""].join(", ");
        case "grade":
          return r.grade || "";
        case "campus":
          return r.campus || "";
        case "providers":
          return (r.providers || []).join(", ");
        case "iep_due_date":
        case "met_due_date":
          return r[sort.col] || "";
        case "type":
          return (r.type || "").toUpperCase();
        case "status":
          return rowStatusPriority(r);
        default:
          return "";
      }
    };
    const copy = [...rawRows];
    copy.sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      let c;
      if (sort.col === "iep_due_date" || sort.col === "met_due_date") {
        c = compareDate(av, bv);
        // Date direction respects asc/desc; missing always sinks because
        // it's coerced to a sentinel above.
        const aMissing = !a[sort.col];
        const bMissing = !b[sort.col];
        if (aMissing !== bMissing) return aMissing ? 1 : -1; // always sink missing
        return c * dir;
      }
      if (sort.col === "status") {
        c = av - bv;
      } else if (typeof av === "number" && typeof bv === "number") {
        c = av - bv;
      } else {
        c = collator.compare(String(av), String(bv));
      }
      return c * dir;
    });
    return copy;
  }, [rawRows, sort.col, sort.dir]);

  const applyRange = (key) => {
    const { start, end } = rangePreset(key);
    setFilters((f) => ({ ...f, start_date: start, end_date: end }));
  };

  const clearRange = () => setFilters((f) => ({ ...f, start_date: "", end_date: "" }));

  // Month grouping for the bottom calendar-ish view.
  const monthGroups = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const d = r.iep_due_date || r.met_due_date;
      if (!d) continue;
      const key = d.slice(0, 7); // YYYY-MM
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(r);
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);

  // CSV export.
  const exportCsv = () => {
    if (!rows.length) {
      toast.info("No rows to export.");
      return;
    }
    const header = [
      "last_name", "first_name", "grade", "campus", "providers",
      "iep_due_date", "iep_status", "iep_days_offset",
      "met_due_date", "met_status", "met_days_offset",
      "type", "is_archived",
    ];
    const lines = [header.join(",")];
    for (const r of rows) {
      const safe = (v) => {
        const s = String(v ?? "");
        return s.includes(",") || s.includes('"')
          ? `"${s.replace(/"/g, '""')}"`
          : s;
      };
      lines.push([
        r.last_name, r.first_name, r.grade, r.campus,
        (r.providers || []).join("|"),
        r.iep_due_date, r.iep_status, r.iep_days_offset ?? "",
        r.met_due_date, r.met_status, r.met_days_offset ?? "",
        r.type, r.is_archived ? "true" : "false",
      ].map(safe).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `due_dates_${ymd(new Date())}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} row${rows.length === 1 ? "" : "s"}.`);
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl space-y-5" data-testid="due-dates-page">
      <header>
        <h1
          className="text-2xl font-semibold text-[#1F2937] heading-font flex items-center gap-2"
          data-testid="due-dates-title"
        >
          <CalendarBlank size={22} weight="duotone" className="text-[#1E3F33]" />
          IEP / MET Due Dates
        </h1>
        <p className="text-[12.5px] text-[#6B7280] mt-1">
          Compliance calendar for IEP and MET (reevaluation) due dates.
          Scoped to your caseload, organization, or selected context.
        </p>
      </header>

      {/* Quick range pills */}
      <div
        data-testid="due-dates-quick-range"
        className="flex items-center gap-2 flex-wrap"
      >
        {[
          { key: "this_week", label: "This week" },
          { key: "next_30", label: "Next 30 days" },
          { key: "next_60", label: "Next 60 days" },
          { key: "next_90", label: "Next 90 days" },
          { key: "this_school_year", label: "This school year" },
        ].map((p) => (
          <button
            type="button"
            key={p.key}
            data-testid={`due-dates-range-${p.key}`}
            onClick={() => applyRange(p.key)}
            className="text-[11.5px] uppercase tracking-[0.06em] font-medium rounded-full px-3 py-1 border bg-white border-[#E5E3DB] text-[#3F4756] hover:bg-[#F3F1EC]"
          >
            {p.label}
          </button>
        ))}
        <button
          type="button"
          onClick={clearRange}
          data-testid="due-dates-range-clear"
          className="text-[11px] text-[#6B7280] hover:text-[#1F2937] underline ml-1"
        >
          Clear range
        </button>
      </div>

      {/* Summary band */}
      {loading && !summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}
        </div>
      )}
      {summary && (
        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
          data-testid="due-dates-summary"
        >
          <SummaryCard
            label="IEP overdue"
            value={summary.iep_overdue}
            tone={summary.iep_overdue > 0 ? "danger" : "ok"}
            testid="due-dates-summary-iep-overdue"
          />
          <SummaryCard
            label="MET overdue"
            value={summary.met_overdue}
            tone={summary.met_overdue > 0 ? "danger" : "ok"}
            testid="due-dates-summary-met-overdue"
          />
          <SummaryCard
            label="IEP due next 30d"
            value={summary.iep_due_next_30d}
            tone={summary.iep_due_next_30d > 0 ? "warn" : "neutral"}
            testid="due-dates-summary-iep-30"
          />
          <SummaryCard
            label="MET due next 30d"
            value={summary.met_due_next_30d}
            tone={summary.met_due_next_30d > 0 ? "warn" : "neutral"}
            testid="due-dates-summary-met-30"
          />
          <SummaryCard
            label="Missing IEP date"
            value={summary.iep_missing}
            tone={summary.iep_missing > 0 ? "warn" : "neutral"}
            testid="due-dates-summary-iep-missing"
          />
          <SummaryCard
            label="Missing MET date"
            value={summary.met_missing}
            tone={summary.met_missing > 0 ? "warn" : "neutral"}
            testid="due-dates-summary-met-missing"
          />
        </div>
      )}

      {/* Filter strip */}
      <Card className="border-[#E5E3DB]" data-testid="due-dates-filters">
        <CardContent className="p-4 flex items-end gap-3 flex-wrap">
          <FunnelSimple size={14} weight="duotone" className="text-[#1E3F33] mb-2" />
          <div className="min-w-[140px]">
            <label className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]">
              Start date
            </label>
            <Input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters((f) => ({ ...f, start_date: e.target.value }))}
              data-testid="due-dates-filter-start"
              className="h-8 text-[12px]"
            />
          </div>
          <div className="min-w-[140px]">
            <label className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]">
              End date
            </label>
            <Input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters((f) => ({ ...f, end_date: e.target.value }))}
              data-testid="due-dates-filter-end"
              className="h-8 text-[12px]"
            />
          </div>
          <div className="min-w-[120px]">
            <label className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]">
              Due type
            </label>
            <Select
              value={filters.due_type}
              onValueChange={(v) => setFilters((f) => ({ ...f, due_type: v }))}
            >
              <SelectTrigger data-testid="due-dates-filter-type" className="h-8 text-[12px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                <SelectItem value="iep">IEP</SelectItem>
                <SelectItem value="met">MET</SelectItem>
                <SelectItem value="both">Both IEP + MET</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[150px]">
            <label className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]">
              Status
            </label>
            <Select
              value={filters.status}
              onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}
            >
              <SelectTrigger data-testid="due-dates-filter-status" className="h-8 text-[12px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                <SelectItem value="overdue">Overdue (any)</SelectItem>
                <SelectItem value="due_soon">Due soon (any)</SelectItem>
                <SelectItem value="iep_overdue">IEP overdue</SelectItem>
                <SelectItem value="met_overdue">MET overdue</SelectItem>
                <SelectItem value="missing_iep">Missing IEP date</SelectItem>
                <SelectItem value="missing_met">Missing MET date</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[100px]">
            <label className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]">
              Grade
            </label>
            <Input
              value={filters.grade}
              onChange={(e) => setFilters((f) => ({ ...f, grade: e.target.value }))}
              placeholder="any"
              data-testid="due-dates-filter-grade"
              className="h-8 text-[12px]"
            />
          </div>
          <div className="min-w-[140px]">
            <label className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]">
              Campus
            </label>
            <Input
              value={filters.campus}
              onChange={(e) => setFilters((f) => ({ ...f, campus: e.target.value }))}
              placeholder="any"
              data-testid="due-dates-filter-campus"
              className="h-8 text-[12px]"
            />
          </div>
          <label className="flex items-center gap-1.5 text-[11.5px] text-[#3F4756] cursor-pointer mb-2">
            <Checkbox
              data-testid="due-dates-filter-archived"
              checked={filters.include_archived}
              onCheckedChange={(c) =>
                setFilters((f) => ({ ...f, include_archived: !!c }))
              }
              className="border-[#D4D2CC]"
            />
            Include archived
          </label>
          <div className="ml-auto flex items-center gap-2">
            <RefreshChip
              dataUpdatedAt={dataUpdatedAt}
              isFetching={refetching}
              onRefresh={load}
              testid="due-dates-refresh"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={exportCsv}
              disabled={!rows.length}
              data-testid="due-dates-export"
              className="h-8 text-[12px] border-[#E5E3DB] text-[#3F4756] disabled:opacity-50"
            >
              <DownloadSimple size={13} className="mr-1" /> Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Error / loading / empty / table */}
      {error && (
        <Card className="border-[#F4D1D1] bg-[#FBEFE8]" data-testid="due-dates-error-card">
          <CardContent className="p-5 flex items-start gap-3">
            <Warning size={18} weight="duotone" className="text-[#A8453A] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-[13px] text-[#7A3A2C] font-semibold">Could not load due dates</p>
              <p className="text-[12px] text-[#7A3A2C] mt-1">{error}</p>
              <Button
                type="button"
                size="sm"
                onClick={load}
                data-testid="due-dates-retry"
                className="mt-3 bg-[#1E3F33] hover:bg-[#173127] text-white h-8 text-[12px]"
              >
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!error && loading && !data && (
        <div className="space-y-2" data-testid="due-dates-loading">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12 rounded" />)}
        </div>
      )}

      {!error && !loading && rows.length === 0 && (
        <Card className="border-dashed border-[#E5E3DB]" data-testid="due-dates-empty">
          <CardContent className="p-8 text-center">
            <CheckCircle size={20} weight="duotone" className="text-[#1E3F33] mx-auto mb-1" />
            <p className="text-[13.5px] text-[#1F2937] mb-1">No matching rows.</p>
            <p className="text-[12px] text-[#6B7280]">
              Adjust the filters above or expand the date range.
            </p>
          </CardContent>
        </Card>
      )}

      {!error && rows.length > 0 && (
        <Card className="border-[#E5E3DB] overflow-hidden" data-testid="due-dates-table-card">
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]" data-testid="due-dates-table">
              <thead className="bg-[#F3F1EC] text-[10.5px] uppercase tracking-[0.08em] text-[#4B5563]">
                <tr>
                  <SortableTh col="student" sort={sort} onClick={onHeaderClick}>Student</SortableTh>
                  <SortableTh col="grade" sort={sort} onClick={onHeaderClick}>Grade</SortableTh>
                  <SortableTh col="campus" sort={sort} onClick={onHeaderClick}>Campus</SortableTh>
                  <SortableTh col="providers" sort={sort} onClick={onHeaderClick}>Provider(s)</SortableTh>
                  <SortableTh col="iep_due_date" sort={sort} onClick={onHeaderClick}>IEP due</SortableTh>
                  <SortableTh col="met_due_date" sort={sort} onClick={onHeaderClick}>MET due</SortableTh>
                  <SortableTh col="type" sort={sort} onClick={onHeaderClick}>Type</SortableTh>
                  <SortableTh col="status" sort={sort} onClick={onHeaderClick}>Status</SortableTh>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.student_id}
                    data-testid={`due-dates-row-${r.student_id}`}
                    className="border-t border-[#F1F2F4] hover:bg-[#F9F8F6]"
                  >
                    <td className="px-3 py-2">
                      <Link
                        to={`/students/${r.student_id}`}
                        className="text-[#1E3F33] hover:underline"
                      >
                        {r.last_name}, {r.first_name}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-[#3F4756]">{r.grade || "—"}</td>
                    <td className="px-3 py-2 text-[#3F4756]">{r.campus || "—"}</td>
                    <td className="px-3 py-2 text-[#3F4756]">
                      {(r.providers || []).join(", ") || "—"}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-[#3F4756]">
                      {fmtDate(r.iep_due_date)}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-[#3F4756]">
                      {fmtDate(r.met_due_date)}
                    </td>
                    <td className="px-3 py-2 uppercase text-[10.5px] tracking-[0.08em] text-[#5C7088]">
                      {r.type}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-0.5">
                        {r.iep_status !== "missing" && (
                          <StatusPill status={r.iep_status} daysOffset={r.iep_days_offset} />
                        )}
                        {r.met_status !== "missing" && (
                          <StatusPill status={r.met_status} daysOffset={r.met_days_offset} />
                        )}
                        {(r.iep_status === "missing" && r.met_status === "missing") && (
                          <StatusPill status="missing" />
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        to={`/students/${r.student_id}`}
                        className="text-[#1E3F33] hover:underline inline-flex items-center text-[11px]"
                      >
                        Open <CaretRight size={11} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Month grouping — collapsible sections at the bottom. */}
      {!error && monthGroups.length > 0 && (
        <details
          className="rounded-md border border-[#E5E3DB] bg-white"
          data-testid="due-dates-month-groups"
        >
          <summary
            className="cursor-pointer list-none px-4 py-2 text-[12px] font-medium text-[#1F2937] flex items-center justify-between"
            data-testid="due-dates-month-groups-summary"
          >
            <span className="inline-flex items-center gap-1.5">
              <Clock size={13} weight="duotone" className="text-[#1E3F33]" />
              Group by month ({monthGroups.length})
            </span>
            <CaretRight size={11} weight="bold" className="text-[#6B7280]" />
          </summary>
          <div className="border-t border-[#E5E3DB] divide-y divide-[#F1F2F4]">
            {monthGroups.map(([month, mrows]) => (
              <div
                key={month}
                className="px-4 py-2"
                data-testid={`due-dates-month-${month}`}
              >
                <p className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#4B5563] mb-1">
                  {new Date(`${month}-01T00:00:00`).toLocaleDateString(undefined, {
                    month: "long", year: "numeric",
                  })}{" "}
                  · {mrows.length} student{mrows.length === 1 ? "" : "s"}
                </p>
                <ul className="text-[12px] text-[#3F4756] space-y-0.5">
                  {mrows.slice(0, 12).map((r) => (
                    <li key={`${month}-${r.student_id}`}>
                      <Link
                        to={`/students/${r.student_id}`}
                        className="hover:underline"
                      >
                        {r.last_name}, {r.first_name}
                      </Link>{" "}
                      <span className="text-[#6B7280]">
                        — {fmtDate(r.iep_due_date || r.met_due_date)} ({r.type})
                      </span>
                    </li>
                  ))}
                  {mrows.length > 12 && (
                    <li className="text-[11px] text-[#6B7280] italic">
                      + {mrows.length - 12} more.
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
